import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useTopicRoutes } from './useTopicRoutes';
import { useReportRoutes } from './useReportRoutes';
import { Routes, defaultPath } from '../config/routes';
import { profileTypes } from '../config/constants/userConstants';

const defaultRoutes = {
  [profileTypes.ADMIN.id]: '/dashboard',
  [profileTypes.OPERATOR.id]: '/calendar',
};

export const useAppRoutes = () => {
  const topicRoutes = useTopicRoutes();
  const reportRoutes = useReportRoutes();
  const { currentUserProfile, permissions, accountModules, customReports, solutionId } = useSelector((state) => ({
    currentUserProfile: state.auth.currentUser?.profile?.idProfile,
    permissions: state.auth.permissions,
    accountModules: state.auth.accountModules,
    customReports: state.customReports.data,
    solutionId: state.auth.accountData?.solutionId,
  }));

  const userRoutes = useMemo(() => {
    const filterRoutes = (routes) => {
      const checkPermissions = (route) => {
        if (!route.profiles.includes(currentUserProfile) && !permissions.manageAccounts) {
          return false;
        }
        if (route.moduleName != null && !accountModules?.[route.moduleName]) {
          return false;
        }
        if (!customReports.total && route.path === '/reports') {
          return false;
        }
        if (route.solutionIds?.length && !route.solutionIds.includes(solutionId)) {
          return false;
        }
        if (typeof route.checkPermissions !== 'function') {
          return true;
        }
        return route.checkPermissions(permissions, currentUserProfile);
      };

      return routes.reduce((newRoutes, route) => {
        if (checkPermissions(route)) {
          const children = route.children && filterRoutes(route.children);
          if (!route.children || children?.length) {
            newRoutes.push({ ...route, children });
            if (typeof route.getSiblings === 'function') {
              newRoutes.push(...route.getSiblings({ topics: topicRoutes }));
            }
          }
        }
        return newRoutes;
      }, []);
    };
    const finalRoutes = filterRoutes(Routes);
    if (reportRoutes?.length) finalRoutes.push(...reportRoutes);
    return finalRoutes;
  }, [currentUserProfile, permissions, accountModules, customReports, solutionId, topicRoutes, reportRoutes]);

  return {
    userRoutes,
    defaultRoute: defaultRoutes[currentUserProfile] ?? userRoutes[0]?.path ?? defaultPath,
  };
};
